import React from 'react';
import { Box, Typography, TextField, IconButton, Link } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';

const DetectionInfo = ({ aiDetectionResult, editableSku, handleSkuChange, handleSkuRefresh, productUrl }) => {
  if (!aiDetectionResult) return null;

  const confidence = aiDetectionResult.confidence || 0;
  const confidenceBg = confidence > 0.8 ? 'success.light' : confidence > 0.6 ? 'warning.light' : 'error.light';
  const dp = aiDetectionResult.detectedProduct || {};
  const shippingInfo = dp.shippingInfo || {};

  // Use name for the title
  const formattedName = dp.name;

  // Product URL is provided from Ashley data (if available)

  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      {/* Confidence Rating at the top */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box sx={{ px: 2, py: 0.5, bgcolor: confidenceBg, borderRadius: 1 }}>
          <Typography variant="body2" color="text.primary" fontWeight="bold">
            {Math.round(confidence * 100)}% Confidence
          </Typography>
        </Box>
      </Box>

      {/* Product Title */}
      <Typography variant="h6" sx={{ mb: 1, textAlign: 'center', fontWeight: 'bold' }}>
        {formattedName || dp.name}
      </Typography>

      {/* Brand */}
      {dp.brand && (
        <Typography variant="body2" color="primary" sx={{ mb: 1, textAlign: 'center' }}>
          🏷️ Brand: {dp.brand}
        </Typography>
      )}

      {/* Editable SKU Field with Refresh Button */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          fullWidth
          label="SKU"
          value={editableSku}
          onChange={(e) => handleSkuChange(e.target.value)}
          placeholder="Enter or edit SKU to search inventory and customers"
          variant="outlined"
          size="small"
          sx={{ 
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              fontWeight: 'bold'
            }
          }}
        />
        <IconButton
          onClick={handleSkuRefresh}
          size="small"
          sx={{ 
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'primary.dark'
            }
          }}
          title="Refresh - Re-scan inventory and customers"
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Generated Product URL */}
      {productUrl && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.08)', borderRadius: 1, border: '1px solid', borderColor: 'success.light' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LinkIcon sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
              GENERATED PRODUCT URL
            </Typography>
          </Box>
          <Link 
            href={productUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ 
              fontSize: '0.75rem', 
              wordBreak: 'break-all',
              display: 'block',
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            {productUrl}
          </Link>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.5, display: 'block' }}>
            Click to view on website (opens in new tab)
          </Typography>
        </Box>
      )}

      {/* Single column with all details */}
      <Box sx={{ p: 1.5, bgcolor: 'rgba(23, 118, 209, 0.08)', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          PRODUCT DETAILS
        </Typography>
        
        {/* Single column layout for all details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {dp.description && (
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              <strong>Description:</strong> {dp.description}
            </Typography>
          )}
          {dp.barcode && (
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              <strong>Barcode:</strong> {dp.barcode}
            </Typography>
          )}
          {dp.trackingCode && (
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              <strong>Tracking:</strong> {dp.trackingCode}
            </Typography>
          )}
          {dp.quantity && (
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              <strong>Quantity:</strong> {dp.quantity}
            </Typography>
          )}
          {dp.poNumber && (
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              <strong>PO:</strong> {dp.poNumber}
            </Typography>
          )}
          {dp.trip && (
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              <strong>Trip:</strong> {dp.trip}
            </Typography>
          )}
          {dp.weight && (
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              <strong>Weight:</strong> {dp.weight} lbs
            </Typography>
          )}
          {dp.cubes && (
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              <strong>Cubes:</strong> {dp.cubes}
            </Typography>
          )}
          
          {/* Shipping info in same column */}
          {shippingInfo.customerName && (
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              <strong>Customer:</strong> {shippingInfo.customerName}
            </Typography>
          )}
          
          {/* Destination in one line */}
          {shippingInfo.destination && (
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              <strong>Destination:</strong> {[
                shippingInfo.destination.name,
                shippingInfo.destination.address,
                shippingInfo.destination.city,
                shippingInfo.destination.state,
                shippingInfo.destination.zipCode
              ].filter(Boolean).join(', ')}
            </Typography>
          )}

          {/* Shipper in one line */}
          {shippingInfo.shipper && (
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              <strong>Shipper:</strong> {[
                shippingInfo.shipper.name,
                shippingInfo.shipper.address,
                shippingInfo.shipper.city,
                shippingInfo.shipper.state,
                shippingInfo.shipper.zipCode
              ].filter(Boolean).join(', ')}
            </Typography>
          )}

          {/* Specifications in same column */}
          {dp.specifications && (
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              <strong>Specs:</strong> {[
                dp.specifications.material && `Material: ${dp.specifications.material}`,
                dp.specifications.color && `Color: ${dp.specifications.color}`,
                dp.specifications.style && `Style: ${dp.specifications.style}`
              ].filter(Boolean).join(' • ')}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DetectionInfo;
